const React = require('react');
const T = require('prop-types');

const BottomButtonContainer = require('components/BottomButtonContainer');
const LinearProgress = require('components/LinearProgress');
const SimpleDialog = require('components/SimpleDialog');
const useForm = require('hooks/useForm');
const PreapprovedEmailUploadFormConfig = require('./formConfig');
const { default: Typography } = require('@mui/material/Typography');

const {
    useMemo,
    useState,
    useEffect,
    useRef
} = React;

const FORM_ID = 'preapproved-email-upload-form';

const internals = {};
internals.buildDefaults = PreapprovedEmailUploadFormConfig.buildDefaults;
internals.buildFields = PreapprovedEmailUploadFormConfig.buildFields;

module.exports = function PreapprovedEmailUpload(props) {

    const {
        api,
        schoolId,
        schoolName,
        schoolRoles,
        onSubmit
    } = props;

    const [showProcessingDialog, setShowProcessingDialog] = useState(false);
    const [csvDuplicates, setCsvDuplicates] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [processingCacheKey, setProcessingCacheKey] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const [userByTextValue, setUserByTextValue] = useState('');
    const [csvValue, setCsvValue] = useState(null);

    const defaults = useMemo(() => PreapprovedEmailUploadFormConfig.buildDefaults(), []);

    const submit = async (data) => {

        const payload = {
            csv: data.csv,
            notifyUsers: data.notifyUsers,
            roleName: data.roleName,
            userByText: data.userByText,
            schoolId
        };

        try {
            setUploadError(null);
            setShowProcessingDialog(true);

            const {
                payload: {
                    result: {
                        httpPost,
                        csvDuplicates: newCsvDuplicates,
                        totalRows: newTotalRows
                    } = {}
                }
            } = await onSubmit(payload);

            setCsvDuplicates(newCsvDuplicates || []);
            setTotalRows(newTotalRows || 0);

            const uploadResponse = await httpPost;

            setProcessingCacheKey(internals.getProcessingKeyFromUploadResponse(uploadResponse));
        }
        catch (err) {
            setUploadError(`Error: ${err.response?.data?.message || err.message || 'Unable to process preapproved users'}`);
        }
    };

    const formConfig = useMemo(() => PreapprovedEmailUploadFormConfig.buildFormConfig({
        formId: FORM_ID,
        title: 'Preapprove Users',
        onSubmit: submit,
        defaults,
        csvValue,
        schoolName,
        schoolRoles,
        setCsvValue,
        setUserByTextValue,
        userByTextValue
    }), [csvValue, defaults, schoolName, schoolRoles, submit, userByTextValue]);

    const {
        methods,
        Form
    } = useForm(formConfig);

    const resetForm = () => {

        setUploadError(null);
        setCsvDuplicates([]);
        setTotalRows(0);
        setProcessingCacheKey(null);
        setUserByTextValue('');
        setCsvValue(null);
        methods.reset(defaults);
    };

    return <BottomButtonContainer
        btnLabel='Create&nbsp;User(s)'
        formId={FORM_ID}
        onBtnClick={methods.handleSubmit(submit)}
        disabled={methods.formState.isSubmitting}
    >
        {Form}
        <SimpleDialog
            isOpen={showProcessingDialog}
            onConfirm={() => {

                resetForm();
                setShowProcessingDialog(false);
            }}
            title={`${uploadError ? 'Error ' : ''}Processing CSV`}
            content={(
                <internals.PreapprovedProcessingDetails
                    totalRows={totalRows || 0}
                    csvDuplicates={csvDuplicates || []}
                    processingCacheKey={processingCacheKey || null}
                    checkProgress={api.communication.checkPreapprovedProgress}
                    error={uploadError}
                />
            )}
        />
    </BottomButtonContainer>;
};

module.exports.propTypes = {
    api: T.any.isRequired,
    schoolId: T.number.isRequired,
    schoolName: T.string.isRequired,
    schoolRoles: T.array,
    onSubmit: T.func.isRequired
};

internals.getProcessingKeyFromUploadResponse = (response = {}) => response.data?.processingKey
    || response.data?.processingCacheKey
    || null;

internals.getProcessingOutcome = ({ status, isComplete, processedCount, totalRows, error }) => {

    if (status === 'failed' || status === 'dead') {
        return {
            terminal: true,
            isComplete: false,
            error: error || 'Preapproved CSV processing failed'
        };
    }

    if (status) {
        const complete = status === 'completed' || Boolean(isComplete);

        return {
            terminal: complete,
            isComplete: complete,
            error: null
        };
    }

    const complete = Boolean(isComplete)
        || processedCount === null
        || typeof processedCount === 'undefined'
        || processedCount === totalRows;

    return {
        terminal: complete,
        isComplete: complete,
        error: null
    };
};

internals.getProcessingBreakdown = (state = {}) => ({
    processedCount: Number(state.processedCount || 0),
    insertCount: Number(state.insertCount || 0),
    updateCount: Number(state.updateCount || 0),
    unchangedCount: Number(state.unchangedCount || 0),
    emailQueuedCount: Number(state.emailQueuedCount || 0),
    emailCompletedCount: Number(state.emailCompletedCount || 0),
    emailFailedCount: Number(state.emailFailedCount || 0)
});

const CHECK_PROGRESS_INTERVAL = 1000;

internals.PreapprovedProcessingDetails = function PreapprovedProcessingDetails(props) {

    const {
        totalRows = 0,
        csvDuplicates,
        processingCacheKey,
        checkProgress,
        error
    } = props;

    const {
        formatIntegerWithCommas
    } = internals;

    const [processedCount, setProcessedCount] = useState(0);
    const [insertCount, setInsertCount] = useState(0);
    const [updateCount, setUpdateCount] = useState(0);
    const [unchangedCount, setUnchangedCount] = useState(0);
    const [emailQueuedCount, setEmailQueuedCount] = useState(0);
    const [emailCompletedCount, setEmailCompletedCount] = useState(0);
    const [emailFailedCount, setEmailFailedCount] = useState(0);
    const [erroredCount, setErroredCount] = useState(0);
    const [alreadyInvitedOrActiveCount, setAlreadyInvitedOrActiveCount] = useState(0);

    const [isComplete, setIsComplete] = useState(false);
    const [processingError, setProcessingError] = useState(null);
    const interval = useRef(null);

    useEffect(() => {

        if (processingCacheKey) {
            clearInterval(interval.current);
            setProcessingError(null);
            setIsComplete(false);

            interval.current = setInterval(async () => {

                const { data = {} } = await checkProgress(processingCacheKey);
                const {
                    processedCount: newProcessedCount,
                    erroredCount: newErroredCount,
                    alreadyInvitedOrActiveCount: newAlreadyInvitedOrActiveCount,
                    status,
                    isComplete: newIsComplete,
                    error: newError
                } = data;

                if (newProcessedCount !== null && typeof newProcessedCount !== 'undefined') {
                    const breakdown = internals.getProcessingBreakdown(data);

                    setProcessedCount(breakdown.processedCount);
                    setInsertCount(breakdown.insertCount);
                    setUpdateCount(breakdown.updateCount);
                    setUnchangedCount(breakdown.unchangedCount);
                    setEmailQueuedCount(breakdown.emailQueuedCount);
                    setEmailCompletedCount(breakdown.emailCompletedCount);
                    setEmailFailedCount(breakdown.emailFailedCount);
                    setErroredCount(newErroredCount);
                    setAlreadyInvitedOrActiveCount(newAlreadyInvitedOrActiveCount || 0);
                }

                const outcome = internals.getProcessingOutcome({
                    status,
                    isComplete: newIsComplete,
                    processedCount: newProcessedCount,
                    totalRows,
                    error: newError
                });

                if (outcome.error) {
                    setProcessingError(outcome.error);
                }

                if (outcome.terminal) {
                    clearInterval(interval.current);
                    setIsComplete(outcome.isComplete);
                }

            }, CHECK_PROGRESS_INTERVAL);
        }

        return () => clearInterval(interval.current);
    }, [processingCacheKey]);

    // Log csvDuplicates in case the uploader wants the emails
    useEffect(() => {

        if (csvDuplicates?.length) {
            console.log('csvDuplicates', csvDuplicates);
        }
    }, [csvDuplicates?.length]);

    const progress = totalRows === 0
        ? 0
        : Number(processedCount / totalRows).toFixed(2) * 100;
    const effectiveError = error || processingError;

    return (
        <div>
            <div style={{ opacity: effectiveError ? 0.3 : 1 }}>
                <LinearProgress value={progress || 0} />
                <Typography style={{ marginTop: 12 }}>Total Rows: {formatIntegerWithCommas(totalRows || 0)}</Typography>
                <Typography>Rows Processed: {formatIntegerWithCommas(processedCount || 0)}</Typography>
                <Typography style={{ marginTop: 8 }}>Users Created: {formatIntegerWithCommas(insertCount || 0)}</Typography>
                <Typography>Users Updated: {formatIntegerWithCommas(updateCount || 0)}</Typography>
                <Typography>Users Unchanged: {formatIntegerWithCommas(unchangedCount || 0)}</Typography>
                <Typography>Already Invited or Active: {formatIntegerWithCommas(alreadyInvitedOrActiveCount || 0)}</Typography>
                <Typography>CSV Invalid: {formatIntegerWithCommas(erroredCount || 0)}</Typography>
                <Typography>CSV Duplicates: {formatIntegerWithCommas(csvDuplicates?.length || 0)}</Typography>
                <Typography style={{ marginTop: 8 }}>Emails Queued: {formatIntegerWithCommas(emailQueuedCount || 0)}</Typography>
                <Typography>Emails Completed: {formatIntegerWithCommas(emailCompletedCount || 0)}</Typography>
                <Typography>Email Failures: {formatIntegerWithCommas(emailFailedCount || 0)}</Typography>
                {!effectiveError && !isComplete && <Typography color='text.secondary' style={{ marginTop: 12 }}>Processing...</Typography>}
                {!effectiveError && isComplete && <Typography color='green' style={{ marginTop: 12 }}>Processing Complete!</Typography>}
            </div>
            {effectiveError && <Typography color='red' style={{ marginTop: 12 }}>{effectiveError}</Typography>}
        </div>
    );
};

internals.PreapprovedProcessingDetails.propTypes = {
    checkProgress: T.func,
    totalRows: T.number,
    csvDuplicates: T.array,
    processingCacheKey: T.string,
    error: T.string
};

internals.formatIntegerWithCommas = (num) => {

    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

module.exports.internals = internals;
